/**
 * /auth/reset-password — landing page after the Supabase
 * password-reset email. When the user clicks the email link,
 * Supabase commits a "recovery" session (full access scope but
 * surfaced through `onAuthStateChange` with event = 'PASSWORD_RECOVERY').
 *
 * This page shows a single "new password" form and calls
 * `supabase.auth.updateUser({ password })`. On success the user is
 * sent to /login.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { getSupabase, isSupabaseConfigured } from '@/api/supabase';

export function AuthResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t('errors.password_short', { defaultValue: 'Use at least 8 characters.' }));
      return;
    }
    if (!isSupabaseConfigured()) {
      setError(t('errors.auth_unconfigured', { defaultValue: 'Auth is not configured.' }));
      return;
    }
    setSubmitting(true);
    try {
      const sb = getSupabase();
      const { error: err } = await sb.auth.updateUser({ password });
      if (err) throw err;
      setDone(true);
      // Sign the user out so the next /login is a fresh credential-
      // check rather than a silent passthrough on the recovery session.
      await sb.auth.signOut().catch(() => {});
      setTimeout(() => navigate('/login?reset=1', { replace: true }), 800);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'reset_failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="space-y-2 text-center">
        <h2 className="text-lg font-semibold tracking-tight">
          {t('reset.success_heading', { defaultValue: 'Password updated' })}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('reset.success_body', { defaultValue: 'Redirecting to login…' })}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="text-center">
        <h2 className="text-lg font-semibold tracking-tight">
          {t('reset.heading', { defaultValue: 'Set a new password' })}
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          {t('reset.subheading', {
            defaultValue: 'Enter a new password for your account. Minimum 8 characters.',
          })}
        </p>
      </div>

      <div>
        <label htmlFor="reset-password" className="mb-1 block text-sm font-medium text-ink">
          {t('reset.password_label', { defaultValue: 'New password' })}
        </label>
        <input
          id="reset-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
        />
      </div>

      {error ? (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <Button type="submit" className="w-full" size="lg" disabled={submitting}>
        {t('reset.submit', { defaultValue: 'Update password' })}
      </Button>

      <p className="text-center text-sm text-ink-muted">
        <Link to="/login" className="font-medium text-spark hover:underline">
          {t('reset.back_to_login', { defaultValue: 'Back to login' })}
        </Link>
      </p>
    </form>
  );
}
